import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Grid,
    Alert,
    Card,
    CardContent,
    Divider,
    IconButton,
    InputAdornment,
    Fade,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import {
    VerifiedUser as VerifiedUserIcon,
    Lock as LockIcon,
    Timer as TimerIcon,
    Send as SendIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useUserAuth } from '../../context/UserAuthContext';

const ProfileKyc = () => {
    const { user, token } = useUserAuth()
    const merchant_id = user?.uid;
    const [step, setStep] = useState(0);
    const [aadhaarNumber, setAadhaarNumber] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [referenceId, setReferenceId] = useState('');
    const [timer, setTimer] = useState(60);
    const [timerActive, setTimerActive] = useState(false);
    const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
    const [verificationData, setVerificationData] = useState(null);

    const getCurrentProfileStatus = async () => {
        // setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${base_url}/api/v1/merchant-kyc/status/${merchant_id}`,);
            console.log('status', response.data)
            if (response.data.data.status == "verified") {
                setStep(3);
                setVerificationData(response.data.data);
                setSuccess('Aadhaar verified successfully! Your KYC has been completed.');
            } else
            {
                setStep(1)
            }
        } catch (err) {
            console.error('Profile current status error:', err);
            // setError(err.response?.data?.message || 'Profile current status error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!merchant_id) return
        getCurrentProfileStatus()
    }, [merchant_id])

    useEffect(() => {
        let interval;
        if (timerActive && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setTimerActive(false);
        }
        return () => clearInterval(interval);
    }, [timerActive, timer]);

    const handleAadhaarChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        if (value.length <= 12) {
            setAadhaarNumber(value);
            setError('');
        }
    };

    const handleOtpChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (value !== '' && index < 5) {
                document.getElementById(`otp-input-${index + 1}`).focus();
            }
        }
    };

    const validateAadhaar = (aadhaar) => {
        const aadhaarPattern = /^\d{12}$/;
        if (!aadhaarPattern.test(aadhaar)) {
            return 'Please enter a valid 12-digit Aadhaar number';
        }
        return '';
    };

    const sendOtp = async () => {
        const validationError = validateAadhaar(aadhaarNumber);
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const payload = {
                aadhaar_number: aadhaarNumber,
                consent: "Y",
                reason: "For KYC"
            };
            const response = await axios.post(
                `${base_url}/api/v1/merchant-kyc/initiate`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            console.log('aadhaar verification', response.data)
            if (response.data.success) {
                setReferenceId(response.data.data.reference_id);
                setStep(2);
                setTimer(60);
                setTimerActive(true);
                setSuccess('OTP sent successfully to your registered mobile number');
            } else {
                setError(response.data.message || 'Failed to send OTP. Please try again.');
            }
        } catch (err) {
            console.error('Error sending OTP:', err);
            setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async () => {
        const otpValue = otp.join('');
        if (otpValue.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const payload = {
                merchant_id: merchant_id,
                aadhaar_number: aadhaarNumber,
                reference_id: referenceId.toString(),
                otp: otpValue
            };
            const response = await axios.post(
                `${base_url}/api/v1/merchant-kyc/verify`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            console.log('otp verification', response.data)
            if (response.data.success) {
                setStep(3);
                setVerificationData(response.data.data);
                setSuccess('Aadhaar verified successfully! Your KYC has been completed.');
            } else {
                setError(response.data.message || 'OTP verification failed. Please try again.');
            }
        } catch (err) {
            console.error('Error verifying OTP:', err);
            setError(err.response?.data?.message || 'OTP verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = () => {
        setOtp(['', '', '', '', '', '']);
        setTimer(60);
        setTimerActive(true);
        sendOtp();
    };
    const handleReset = () => {
        setStep(1);
        setAadhaarNumber('');
        setOtp(['', '', '', '', '', '']);
        setError('');
        setSuccess('');
        setTimerActive(false);
        setTimer(60);
    };

    const formatAadhaar = (num) => {
        return num.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3');
    };

    return (
        <div className="page-wrapper">
            <div className="container-fluid">
                <div className="row">
          <div className="card">
                      <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
                        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <VerifiedUserIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                                <Typography variant="h4" component="h1" gutterBottom>
                                    Aadhaar KYC Verification
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Complete your KYC verification using Aadhaar
                                </Typography>
                            </Box>
                            <Stepper activeStep={step - 1} sx={{ mb: 4 }}>
                                <Step>
                                    <StepLabel>Enter Aadhaar</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Verify OTP</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel>Complete</StepLabel>
                                </Step>
                            </Stepper>
                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            )}
                            {success && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    {success}
                                </Alert>
                            )}

                            {step === 1 && (
                                <Fade in={step === 1}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Enter Your Aadhaar Number
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Please enter your 12-digit Aadhaar number to receive OTP on your registered mobile number
                                        </Typography>

                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Aadhaar Number"
                                                    variant="outlined"
                                                    value={formatAadhaar(aadhaarNumber)}
                                                    onChange={handleAadhaarChange}
                                                    placeholder="XXXX XXXX XXXX"
                                                    disabled={loading}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <VerifiedUserIcon color="action" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    error={!!error}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                    <Button
                                                        variant="contained"
                                                        size="large"
                                                        onClick={sendOtp}
                                                        disabled={loading || aadhaarNumber.length !== 12}
                                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                                    >
                                                        {loading ? 'Sending OTP...' : 'Send OTP'}
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Typography variant="caption" display="block" sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                                            By continuing, you agree to share your Aadhaar details for verification purposes
                                        </Typography>
                                    </Box>
                                </Fade>
                            )}

                            {step === 2 && (
                                <Fade in={step === 2}>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <IconButton onClick={() => setStep(1)} sx={{ mr: 1 }}>
                                                <ArrowBackIcon />
                                            </IconButton>
                                            <Typography variant="h6">
                                                Verify OTP
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Enter the 6-digit OTP sent to your mobile number linked with Aadhaar:
                                            <strong> {formatAadhaar(aadhaarNumber)}</strong>
                                        </Typography>

                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                                            <Grid container spacing={2} justifyContent="center">
                                                {otp.map((digit, index) => (
                                                    <Grid item key={index}>
                                                        <TextField
                                                            id={`otp-input-${index}`}
                                                            value={digit}
                                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                                            inputProps={{
                                                                maxLength: 1,
                                                                style: { textAlign: 'center', fontSize: '24px' }
                                                            }}
                                                            sx={{
                                                                width: 60,
                                                                '& .MuiInputBase-root': {
                                                                    height: 60
                                                                }
                                                            }}
                                                            disabled={loading}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>

                                        {/* Timer */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
                                            <TimerIcon sx={{ mr: 1, color: timerActive ? 'warning.main' : 'error.main' }} />
                                            <Typography variant="body1" color={timerActive ? 'warning.main' : 'error.main'}>
                                                {timer > 0 ? `OTP expires in ${timer}s` : 'OTP expired'}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                onClick={verifyOtp}
                                                disabled={loading || otp.join('').length !== 6 || !timerActive}
                                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                            >
                                                {loading ? 'Verifying...' : 'Verify OTP'}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                size="large"
                                                onClick={handleResendOtp}
                                                disabled={timerActive || loading}
                                                startIcon={<SendIcon />}
                                            >
                                                Resend OTP
                                            </Button>
                                        </Box>
                                    </Box>
                                </Fade>
                            )}
                            {step === 3 && (
                                <Fade in={step === 3}>
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />

                                        <Typography variant="h4" gutterBottom sx={{ color: 'success.main' }}>
                                            Thank You for Verification!
                                        </Typography>

                                        <Typography variant="h6" paragraph>
                                            Your Aadhaar KYC verification has been completed successfully.
                                        </Typography>

                                        <Typography variant="body1" color="text.secondary" paragraph>
                                            Your verification details have been securely stored in our system.
                                            No sensitive information is displayed here for security purposes.
                                        </Typography>

                                        <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary' }}>
                                            Status: <strong style={{ color: 'green' }}>Verified ✓</strong>
                                        </Typography>
                                        {verificationData && (
                                            <Card sx={{ mt: 4, textAlign: 'left' }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        Verification Details
                                                    </Typography>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Name
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {verificationData.name}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Date of Birth
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {verificationData.date_of_birth}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Status
                                                            </Typography>
                                                            <Typography variant="body1" color="success.main">
                                                                Verified ✓
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        )}
                                    </Box>
                                </Fade>
                            )}
                        </Paper>

                        {/* Loading Overlay */}
                        {loading && (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 9999
                                }}
                            >
                                <CircularProgress size={60} sx={{ color: 'white' }} />
                            </Box>
                        )}
                    </Container>    
          </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileKyc;